import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { 
  Route, BookOpen, Award, ChevronRight, Clock, CheckCircle2, 
  PlayCircle, Loader2, Trophy, Lock, Star, Sparkles, 
  Target, Beaker, ArrowRight, Shield, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
const API = `${API_BASE}/api`;

// EXACT Navigate Learning Academy logo - DO NOT MODIFY
const NLA_LOGO = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/ckawpz0h_Asset%203.png";

// Visual Roadmap Node Component
function RoadmapNode({ item, index, total, status, type = 'course' }) {
  const isCompleted = status === 'completed';
  const isActive = status === 'active';
  const isLocked = status === 'locked';
  const isLast = index === total - 1;
  
  return (
    <div className="relative flex items-start gap-4">
      {/* Vertical Line */}
      {!isLast && (
        <div className="absolute left-6 top-14 w-0.5 h-16 bg-gradient-to-b from-zinc-700 to-zinc-800">
          {isCompleted && (
            <div className="absolute inset-0 bg-gradient-to-b from-[#1EBFB3] to-[#1EBFB3]/50 animate-pulse" />
          )}
        </div>
      )}
      
      {/* Node Circle */}
      <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
        isCompleted 
          ? 'bg-gradient-to-br from-[#1EBFB3] to-[#25D4C7] shadow-lg shadow-[#1EBFB3]/30' 
          : isActive
            ? 'bg-gradient-to-br from-[#C8A85A] to-[#D4B76A] shadow-lg shadow-[#C8A85A]/30 animate-pulse'
            : 'bg-zinc-800 border-2 border-zinc-700'
      }`}>
        {isCompleted ? (
          <CheckCircle2 className="h-6 w-6 text-white" />
        ) : isActive ? (
          <PlayCircle className="h-6 w-6 text-white" />
        ) : isLocked ? (
          <Lock className="h-5 w-5 text-zinc-500" />
        ) : (
          <span className="text-zinc-400 font-bold">{index + 1}</span>
        )}
        
        {/* Completion sparkle */}
        {isCompleted && (
          <div className="absolute -top-1 -right-1">
            <Sparkles className="h-4 w-4 text-[#C8A85A] animate-pulse" />
          </div>
        )}
      </div>
      
      {/* Content Card */}
      <div className={`flex-1 p-4 rounded-xl border transition-all duration-300 ${
        isCompleted 
          ? 'bg-[#1EBFB3]/10 border-[#1EBFB3]/30 hover:border-[#1EBFB3]/50' 
          : isActive
            ? 'bg-[#C8A85A]/10 border-[#C8A85A]/30 hover:border-[#C8A85A]/50'
            : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
      }`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Type Badge */}
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                type === 'lab' 
                  ? 'bg-[#1EBFB3]/20 text-[#1EBFB3]' 
                  : 'bg-zinc-700 text-zinc-300'
              }`}>
                {type === 'lab' ? <Beaker className="h-3 w-3" /> : <BookOpen className="h-3 w-3" />}
                {type === 'lab' ? 'Learning Lab' : 'Course'}
              </span>
              {isCompleted && (
                <span className="text-xs text-[#1EBFB3] font-medium">Completed</span>
              )}
              {isActive && (
                <span className="text-xs text-[#C8A85A] font-medium">In Progress</span>
              )}
            </div>
            
            <h4 className="font-semibold text-white truncate">{item.title}</h4>
            
            <div className="flex items-center gap-3 mt-2 text-xs text-zinc-400">
              {item.duration && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {item.duration}
                </span>
              )}
              {item.estimated_duration_minutes && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {item.estimated_duration_minutes} mins
                </span>
              )}
            </div>
          </div>
          
          {/* Action Button */}
          {isCompleted ? (
            <div className="flex items-center gap-1 text-[#1EBFB3]">
              <CheckCircle2 className="h-5 w-5" />
            </div>
          ) : isActive ? (
            <Link
              to={type === 'lab' ? `/labs/${item.lab_id}` : `/courses/${item.slug || item.course_id}/ai-learn`}
              className="px-4 py-2 bg-[#C8A85A] hover:bg-[#D4B76A] text-black rounded-lg text-sm font-medium flex items-center gap-1 transition-colors"
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </Link>
          ) : (
            <div className="px-3 py-1.5 bg-zinc-800 text-zinc-500 rounded-lg text-xs">
              Locked
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Pathway Badge Card - Only displays if admin has uploaded a badge image
function PathwayBadgeCard({ badge, isEarned, progress }) {
  // Only show if there's an actual badge with an image uploaded by admin
  if (!badge || !badge.image_url) {
    return (
      <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-6 text-center">
        <Crown className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-sm text-zinc-500">No badge assigned to this pathway</p>
        <p className="text-xs text-zinc-600 mt-1">Admin can upload a badge when editing the pathway</p>
      </div>
    );
  }
  
  return (
    <div className={`relative rounded-2xl overflow-hidden transition-all duration-300 ${
      isEarned 
        ? 'bg-gradient-to-br from-[#1EBFB3]/20 via-zinc-900 to-[#C8A85A]/20 border-2 border-[#1EBFB3]/50 shadow-xl shadow-[#1EBFB3]/10' 
        : 'bg-zinc-900/80 border border-zinc-800'
    }`}>
      {/* Glow effect for earned badges */}
      {isEarned && (
        <>
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#1EBFB3]/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-[#C8A85A]/20 rounded-full blur-3xl" />
        </>
      )}
      
      <div className="relative z-10 p-6">
        <div className="flex items-center gap-4">
          {/* Badge Image - Admin uploaded */}
          <div className="relative">
            <div className="relative">
              {isEarned && (
                <div className="absolute inset-0 bg-gradient-to-br from-[#1EBFB3] to-[#C8A85A] rounded-xl blur-md opacity-50" />
              )}
              <img 
                src={badge.image_url} 
                alt={badge.name}
                className={`relative w-24 h-24 rounded-xl object-cover ${!isEarned && 'opacity-40 grayscale'}`}
              />
            </div>
            
            {/* Earned indicator */}
            {isEarned && (
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#1EBFB3] rounded-full flex items-center justify-center shadow-lg">
                <CheckCircle2 className="h-5 w-5 text-white" />
              </div>
            )}
          </div>
          
          {/* Badge Info */}
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white">{badge.name || 'Pathway Badge'}</h3>
            {badge.category && <p className="text-sm text-zinc-400 mt-1">{badge.category}</p>}
            
            {isEarned ? (
              <div className="flex items-center gap-2 mt-3">
                <Sparkles className="h-4 w-4 text-[#C8A85A]" />
                <span className="text-[#C8A85A] font-semibold">Badge Earned!</span>
              </div>
            ) : (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-zinc-500">Progress</span>
                  <span className="text-[#1EBFB3]">{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            )}
          </div>
        </div>
        
        {/* Navigate Learning Academy Branding */}
        <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-zinc-800">
          <img src={NLA_LOGO} alt="Navigate Learning Academy" className="h-6 w-6 object-contain" />
          <span className="text-xs text-zinc-500">Navigate Learning Academy</span>
        </div>
      </div>
    </div>
  );
}

export default function PathwayProgressPage() {
  const { pathwayId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [pathway, setPathway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [checkingCompletion, setCheckingCompletion] = useState(false);

  console.log('PathwayProgressPage LOADED - pathwayId:', pathwayId);
  
  // DEBUG: Show we're in the right component
  if (typeof window !== 'undefined') {
    console.log('🔥 PathwayProgressPage RENDERING with pathwayId:', pathwayId);
    window.PATHWAY_PROGRESS_PAGE_LOADED = true;
  }

  useEffect(() => {
    if (pathwayId) {
      fetchPathwayProgress();
    }
  }, [pathwayId, token]);

  const fetchPathwayProgress = async () => {
    try {
      const response = await axios.get(`${API}/learning-pathways/${pathwayId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setPathway(response.data);
    } catch (error) {
      console.error('Failed to fetch pathway:', error);
      toast.error('Failed to load pathway');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!token) {
      toast.error('Please log in to enroll');
      return;
    }

    setEnrolling(true);
    try {
      const response = await axios.post(
        `${API}/learning-pathways/${pathwayId}/enroll`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Enrolled in ${response.data.enrolled_in} courses!`);
      fetchPathwayProgress();
    } catch (error) {
      toast.error('Failed to enroll in pathway');
    } finally {
      setEnrolling(false);
    }
  };

  const checkCompletion = async () => {
    if (!token) return;
    
    setCheckingCompletion(true);
    try {
      const response = await axios.post(
        `${API}/learning-pathways/${pathwayId}/check-completion`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.pathway_completed) {
        toast.success('Congratulations! You completed the pathway!', {
          description: response.data.badge_awarded ? `Earned: ${response.data.badge_awarded.name}` : undefined
        });
        fetchPathwayProgress();
      } else {
        toast.info(`${response.data.courses_completed}/${response.data.total_courses} courses completed`);
      }
    } catch (error) {
      console.error('Error checking completion:', error);
    } finally {
      setCheckingCompletion(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-[#1EBFB3] animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!pathway) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <Route className="h-16 w-16 text-zinc-600 mb-4" />
          <h2 className="text-xl text-white mb-2">Pathway Not Found</h2>
          <Link to="/pathways" className="text-[#1EBFB3] hover:underline">
            Back to Pathways
          </Link>
        </div>
      </Layout>
    );
  }

  const progress = pathway.progress || { completed: 0, total: 1, percentage: 0 };
  const isPathwayComplete = progress.percentage === 100;
  const hasEnrolled = progress.completed > 0 || pathway.course_details?.some(c => c.status !== 'locked');

  // Combine courses and labs into a single roadmap
  const roadmapItems = [
    ...(pathway.course_details || []).map(c => ({ ...c, type: 'course' })),
    ...(pathway.lab_details || []).map(l => ({ ...l, type: 'lab' }))
  ];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-zinc-400 mb-6">
          <Link to="/pathways" className="hover:text-white transition-colors">Pathways</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-white">{pathway.title}</span>
        </div>

        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#1F3C88]/30 via-zinc-900 to-[#1EBFB3]/20 border border-[#1EBFB3]/20 rounded-2xl p-8 mb-8 overflow-hidden">
          {/* Background decorations */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#1EBFB3]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#C8A85A]/10 rounded-full blur-3xl" />
          
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div className="flex-1">
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#1EBFB3]/20 border border-[#1EBFB3]/30 rounded-full text-sm text-[#1EBFB3] mb-4">
                  <Route className="h-4 w-4" />
                  {pathway.category}
                </span>
                
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{pathway.title}</h1>
                <p className="text-zinc-400 max-w-2xl">{pathway.description}</p>
                
                {/* Stats */}
                <div className="flex flex-wrap items-center gap-6 mt-6">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-zinc-400" />
                    <span className="text-white font-medium">{pathway.courses?.length || 0} Courses</span>
                  </div>
                  {(pathway.labs?.length || 0) > 0 && (
                    <div className="flex items-center gap-2">
                      <Beaker className="h-5 w-5 text-[#1EBFB3]" />
                      <span className="text-white font-medium">{pathway.labs.length} Labs</span>
                    </div>
                  )}
                  {pathway.estimated_duration && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-zinc-400" />
                      <span className="text-white font-medium">{pathway.estimated_duration}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Progress Circle */}
              <div className="flex flex-col items-center">
                <div className="relative w-32 h-32">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-zinc-800"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="url(#progressGradient)"
                      strokeWidth="8"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${progress.percentage * 3.52} 352`}
                      className="transition-all duration-1000"
                    />
                    <defs>
                      <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#1EBFB3" />
                        <stop offset="100%" stopColor="#C8A85A" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold text-white">{Math.round(progress.percentage)}%</span>
                    <span className="text-xs text-zinc-400">Complete</span>
                  </div>
                </div>
                
                {!hasEnrolled ? (
                  <Button
                    onClick={handleEnroll}
                    disabled={enrolling}
                    className="mt-4 bg-gradient-to-r from-[#1EBFB3] to-[#C8A85A] hover:from-[#25D4C7] hover:to-[#D4B76A] text-black font-semibold"
                  >
                    {enrolling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
                    Start Pathway
                  </Button>
                ) : !isPathwayComplete && (
                  <p className="mt-4 text-sm text-zinc-400">
                    {progress.completed}/{progress.total} completed
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Roadmap Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Target className="h-5 w-5 text-[#1EBFB3]" />
                Your Learning Roadmap
              </h2>
              {hasEnrolled && !isPathwayComplete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={checkCompletion}
                  disabled={checkingCompletion}
                  className="border-[#1EBFB3]/50 text-[#1EBFB3] hover:bg-[#1EBFB3]/10"
                >
                  {checkingCompletion ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Check Progress
                </Button>
              )}
            </div>
            
            <div className="space-y-4">
              {roadmapItems.map((item, idx) => (
                <RoadmapNode
                  key={item.course_id || item.lab_id}
                  item={item}
                  index={idx}
                  total={roadmapItems.length}
                  status={item.status || (idx === 0 ? 'active' : 'locked')}
                  type={item.type}
                />
              ))}
              
              {/* Completion Node */}
              <div className="relative flex items-start gap-4 pt-4">
                <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isPathwayComplete 
                    ? 'bg-gradient-to-br from-[#C8A85A] to-[#D4B76A] shadow-lg shadow-[#C8A85A]/30' 
                    : 'bg-zinc-800 border-2 border-dashed border-zinc-700'
                }`}>
                  <Trophy className={`h-6 w-6 ${isPathwayComplete ? 'text-white' : 'text-zinc-600'}`} />
                </div>
                <div className={`flex-1 p-4 rounded-xl border ${
                  isPathwayComplete 
                    ? 'bg-gradient-to-r from-[#C8A85A]/20 to-[#1EBFB3]/20 border-[#C8A85A]/50' 
                    : 'bg-zinc-900/30 border-dashed border-zinc-800'
                }`}>
                  <h4 className={`font-semibold ${isPathwayComplete ? 'text-[#C8A85A]' : 'text-zinc-500'}`}>
                    {isPathwayComplete ? 'Pathway Completed!' : 'Complete All Items to Earn Badge'}
                  </h4>
                  <p className="text-xs text-zinc-500 mt-1">
                    {isPathwayComplete 
                      ? 'Congratulations on your achievement!' 
                      : 'Finish all courses and labs to unlock your Specialist Badge'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Pathway Badge Column */}
          <div className="lg:col-span-1">
            <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
              <Crown className="h-5 w-5 text-[#C8A85A]" />
              Pathway Badge
            </h2>
            
            <PathwayBadgeCard
              badge={pathway.badge_info}
              isEarned={isPathwayComplete && pathway.badge_earned}
              progress={progress.percentage}
            />
            
            {/* XP Info */}
            <div className="mt-6 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
              <h3 className="text-sm font-medium text-white mb-3">Pathway Rewards</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Pathway Completion</span>
                  <span className="text-[#C8A85A] font-semibold">+1000 XP</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Course XP (estimated)</span>
                  <span className="text-[#1EBFB3] font-semibold">+{(pathway.courses?.length || 0) * 500} XP</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Navigate Learning Academy Footer */}
        <div className="mt-12 pt-6 border-t border-zinc-800 flex items-center justify-center gap-3">
          <img src={NLA_LOGO} alt="Navigate Learning Academy" className="h-8 w-8 object-contain" />
          <span className="text-sm text-zinc-500">Powered by Navigate Learning Academy</span>
        </div>
      </div>
    </Layout>
  );
}
